import React, { useState, useEffect } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { connect, useDispatch } from 'react-redux';
import { Button, Spinner, Table, Badge, Card, CardBody } from 'reactstrap';
import Swal from 'sweetalert2';
import { getPlanById } from 'config/subscriptionPlans';
import {
  createGroupPayment,
  confirmGroupPayment,
} from 'redux/GroupOrders/GroupOrdersActions';

const GroupPaymentForm = (props) => {
  const { groupOrderId, order } = props;
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [codes, setCodes] = useState([]);
  const [coordinatorAccountCreated, setCoordinatorAccountCreated] = useState(false);
  const [coordinatorEmail, setCoordinatorEmail] = useState(null);
  const [coordinatorTempPassword, setCoordinatorTempPassword] = useState(null);

  const paymentOptions = {
    layout: {
      type: 'accordion',
      defaultCollapsed: false,
      radios: false,
      spacedAccordionItems: true,
    },
    terms: { card: 'never' },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);

    // Validate the payment element
    const { error: submitError } = await elements.submit();
    if (submitError) {
      Swal.fire('Error', submitError.message, 'error');
      setLoading(false);
      return;
    }

    // Create PaymentIntent on backend
    dispatch(createGroupPayment(groupOrderId));
  };

  // After PaymentIntent is created, confirm with Stripe
  useEffect(() => {
    if (!props.createGroupPaymentState.data?.clientSecret) return;
    if (!stripe || !elements) return;

    const clientSecret = props.createGroupPaymentState.data.clientSecret;

    const confirmPayment = async () => {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        Swal.fire('Payment Failed', error.message, 'error');
        setLoading(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Confirm on backend and generate codes
        dispatch(confirmGroupPayment(groupOrderId, paymentIntent.id));
      }
    };

    confirmPayment();
  }, [props.createGroupPaymentState.data]);

  // After backend confirmation, show codes
  useEffect(() => {
    if (props.confirmGroupPaymentState.success && props.confirmGroupPaymentState.data?.codes) {
      const data = props.confirmGroupPaymentState.data;
      setCodes(data.codes);
      setCoordinatorAccountCreated(data.coordinatorAccountCreated || false);
      setCoordinatorEmail(data.coordinatorEmail || null);
      setCoordinatorTempPassword(data.coordinatorTempPassword || null);
      setPaymentComplete(true);
      setLoading(false);
    }

    if (props.confirmGroupPaymentState.error) {
      Swal.fire('Error', props.confirmGroupPaymentState.error.error || 'Failed to generate codes', 'error');
      setLoading(false);
    }
  }, [props.confirmGroupPaymentState]);

  if (props.createGroupPaymentState.error) {
    return (
      <div className="text-center py-4">
        <p className="text-danger">
          {props.createGroupPaymentState.error.error || 'Failed to create payment'}
        </p>
      </div>
    );
  }

  // After payment: show generated codes
  if (paymentComplete && codes.length > 0) {
    const copyAllCodes = () => {
      const text = codes.map((c) => c.code).join('\n');
      navigator.clipboard.writeText(text).then(() => {
        Swal.fire({
          title: 'Copied!',
          text: `${codes.length} codes copied to clipboard.`,
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
      });
    };

    return (
      <div>
        <div className="text-center mb-4">
          <i className="fas fa-check-circle text-success" style={{ fontSize: '3rem' }} />
          <h3 className="mt-3">Payment Successful!</h3>
          <p className="text-muted">
            Here are the {codes.length} access codes for your residents. Each code can be redeemed once.
          </p>
        </div>

        <div className="d-flex justify-content-end mb-2">
          <Button size="sm" color="primary" onClick={copyAllCodes}>
            <i className="fas fa-copy mr-1" /> Copy All Codes
          </Button>
        </div>

        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <Table size="sm" bordered>
            <thead className="thead-light">
              <tr>
                <th>#</th>
                <th>Access Code</th>
                <th>Access</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c, i) => (
                <tr key={c.id}>
                  <td>{i + 1}</td>
                  <td>
                    <code
                      style={{ fontSize: '1rem', cursor: 'pointer' }}
                      title="Click to copy"
                      onClick={() => {
                        navigator.clipboard.writeText(c.code);
                        Swal.fire({
                          toast: true,
                          position: 'top-end',
                          icon: 'success',
                          title: 'Copied!',
                          showConfirmButton: false,
                          timer: 800,
                        });
                      }}
                    >
                      {c.code} <i className="fas fa-copy text-muted" style={{ fontSize: '0.7rem' }} />
                    </code>
                  </td>
                  <td>
                    <small>{getPlanById(c.planId)?.months || '?'} {getPlanById(c.planId)?.months === 1 ? 'Month' : 'Months'}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {coordinatorAccountCreated && (
          <Card className="border-success mt-3" style={{ borderLeft: '4px solid #2dce89' }}>
            <CardBody className="py-3">
              <h5 className="mb-2">
                <i className="fas fa-user-check text-success mr-2" />
                Coordinator Account Created
              </h5>
              <p className="mb-2" style={{ fontSize: '0.85rem' }}>
                Use these credentials to log in and manage your group codes:
              </p>
              <div
                style={{
                  background: '#fff3cd',
                  padding: '12px 15px',
                  borderRadius: '5px',
                  marginBottom: '12px',
                }}
              >
                <p className="mb-1" style={{ fontSize: '0.9rem' }}>
                  <strong>Email:</strong> {coordinatorEmail}
                </p>
                <p className="mb-0" style={{ fontSize: '0.9rem' }}>
                  <strong>Temporary Password:</strong>{' '}
                  <code style={{ fontSize: '0.95rem', userSelect: 'all' }}>
                    {coordinatorTempPassword}
                  </code>
                  <Button
                    size="sm"
                    color="info"
                    outline
                    className="ml-2 py-0 px-2"
                    style={{ fontSize: '0.75rem' }}
                    onClick={() => {
                      navigator.clipboard.writeText(coordinatorTempPassword);
                      Swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: 'Password copied!',
                        showConfirmButton: false,
                        timer: 800,
                      });
                    }}
                  >
                    <i className="fas fa-copy mr-1" />
                    Copy
                  </Button>
                </p>
              </div>
              <p className="mb-2" style={{ fontSize: '0.85rem' }}>
                <i className="fas fa-envelope text-muted mr-1" />
                These credentials were also sent to <strong>{coordinatorEmail}</strong>.
                If you don't see the email, check your <strong>spam/junk folder</strong>.
              </p>
              <p className="mb-0" style={{ fontSize: '0.85rem', color: '#fb6340' }}>
                <i className="fas fa-exclamation-triangle mr-1" />
                Please change your password after your first login.
              </p>
              <a
                href="/auth/login"
                className="btn btn-success btn-sm mt-2"
              >
                <i className="fas fa-sign-in-alt mr-1" />
                Log In Now
              </a>
            </CardBody>
          </Card>
        )}

        <Card className="bg-warning text-white mt-3">
          <CardBody className="py-3">
            <p className="mb-0" style={{ fontSize: '0.85rem' }}>
              <i className="fas fa-info-circle text-white mr-2" />
              <strong>Next steps:</strong> Share these codes with your residents. They can redeem them
              during registration or on the Plans page after logging in.
              {coordinatorAccountCreated
                ? ' Log in with the credentials above to manage your codes from the Group Codes page.'
                : <> You can also log in with{' '}<strong>{order.contactEmail}</strong> to view your codes anytime from the Group Codes page.</>
              }
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={paymentOptions} />
      <Button
        color="primary"
        className="w-100 mt-4"
        type="submit"
        disabled={!stripe || loading}
        size="lg"
      >
        {loading ? (
          <>
            <Spinner size="sm" className="mr-2" /> Processing...
          </>
        ) : (
          `Pay $${order.totalAmount}`
        )}
      </Button>
    </form>
  );
};

const mapStateToProps = (state) => ({
  createGroupPaymentState: state.createGroupPayment,
  confirmGroupPaymentState: state.confirmGroupPayment,
});

export default connect(mapStateToProps)(GroupPaymentForm);
